import { forkJoin, of } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { get } from 'lodash-es';
import { emptyArray } from 'ui-components/default-props/empty';
import { LogService } from '../../services/log-service';
import Global from '../../store/global-selectors';
import * as SellerActions from '../../store/seller/seller.actions';
import * as Actions from './reference-data.actions';
import * as ReferenceDataApi from './reference-data.api';
import { getPartnerSharedCarrierIds, getPartnerSharedSellerLabelProviders } from '../../store/carrier-sharing/carrier-sharing.api';
import { fixupAllCarriersWithSvgsReferenceData, getCarriersFromIds } from './reference-data-fixup-helpers';
import DataCache from '../../components/data-driven-logo/data-cache';

const logAndReturn = (route) => (e) => {
  LogService.logError(new Error(`Reference Data call for ${route} failed`, { cause: e }));
  return of([]);
};

const callCarrierSharingApi = (carrierSharedEnabled, apiCall) => {
  if (!carrierSharedEnabled) {
    return Promise.resolve(emptyArray);
  }
  return apiCall();
};

export const mergeLabelProviders = (ownLabelProviders, sharedLabelProviders) => {
  const result = [];
  if (sharedLabelProviders?.length > 0) {
    sharedLabelProviders.forEach((labelProvider) => {
      const foundOwnLabelProviders = ownLabelProviders.some((object) => object.labelProviderId === labelProvider.labelProviderId);
      const alreadyInResult = result.some((object) => object.labelProviderId === labelProvider.labelProviderId);
      if (!foundOwnLabelProviders && !alreadyInResult) {
        result.push(labelProvider);
      }
    });
  }
  return result.concat(ownLabelProviders);
};

export const getSellerSuccess = (action$, { getState }) => {
  return action$.pipe(
    ofType(SellerActions.GET_SELLER_SUCCESS, Actions.GET_REFERENCE_DATA),
    mergeMap(() => {
      const state = getState();
      const sellerHomeCountry = Global.selectSellerHomeCountry(state);
      const { isVendorOr3PL } = Global.selectSellerSubscriptionPlanInfo(state);

      return forkJoin(
        ReferenceDataApi.getLabelProviders(sellerHomeCountry).pipe(catchError(logAndReturn('getLabelProviders'))),
        ReferenceDataApi.getSellerCarrierIds().pipe(catchError(logAndReturn('getSellerCarrierIds'))),
        ReferenceDataApi.getAllTheCarriers().pipe(catchError(logAndReturn('getAllTheCarriers'))),
        ReferenceDataApi.getCountries().pipe(catchError(logAndReturn('getCountries'))),
        ReferenceDataApi.getStates().pipe(catchError(logAndReturn('getStates'))),
        ReferenceDataApi.getServiceClassifications().pipe(catchError(logAndReturn('getServiceClassifications'))),
        ReferenceDataApi.getProviderDisplayInfo(sellerHomeCountry).pipe(catchError(logAndReturn('getProviderDisplayInfo'))),
        ReferenceDataApi.getMarketplaceDisplayInfo().pipe(catchError(logAndReturn('getMarketplaceDisplayInfo'))),
        ReferenceDataApi.getFulfillmentProviderDisplayInfo().pipe(catchError(logAndReturn('getMarketplaceDisplayInfo'))),
        callCarrierSharingApi(isVendorOr3PL, getPartnerSharedCarrierIds),
        callCarrierSharingApi(isVendorOr3PL, getPartnerSharedSellerLabelProviders),
        ReferenceDataApi.getServiceReference().pipe(catchError(logAndReturn('getServiceReference'))),
      ).pipe(
        map((calls) => {
          DataCache.setCache({
            labelProviders: get(calls, '0', emptyArray),
            providers: get(calls, '6', emptyArray),
            marketplaces: get(calls, '7', emptyArray),
            fillProviders: get(calls, '8', emptyArray),
          });
          const carrierIds = [...new Set([...get(calls, '1', emptyArray), ...get(calls, '9', emptyArray)])]; // this is to make sure values are distinct
          const allSupportedCarrierIdsAndTheirConfirmations = get(calls, '2', emptyArray);
          const allTheCarriers = fixupAllCarriersWithSvgsReferenceData(allSupportedCarrierIdsAndTheirConfirmations);
          const carriers = getCarriersFromIds(carrierIds, allTheCarriers);
          const labelProviders = mergeLabelProviders(get(calls, '0', emptyArray), get(calls, '10', emptyArray));

          const data = {
            marketplaces: get(calls, '7', emptyArray),
            labelProviders,
            carriers,
            allTheCarriers,
            countries: get(calls, '3', emptyArray),
            states: get(calls, '4', emptyArray),
            serviceClassifications: get(calls, '5', emptyArray),
            fulfillmentProviders: get(calls, '8', emptyArray),
            serviceReference: get(calls, '11', emptyArray),
          };

          return Actions.getReferenceDataSuccess(data);
        }),
        catchError((error) => {
          LogService.logError(new Error('Reference Data failed', { cause: error }));
          return of(Actions.getReferenceDataFailure(error));
        }),
      );
    }),
  );
};
